import React, { useState, useRef } from 'react';
import './lexnote.css';

export default function LexNoteApp() {
  const [transcript, setTranscript] = useState('');
  const [summary, setSummary]       = useState('');
  const [status, setStatus]         = useState('idle'); // idle|recording|transcribing|summarizing
  const [progress, setProgress]     = useState(0);

  const videoRef    = useRef(null);
  const recorderRef = useRef(null);
  const streamRef   = useRef(null);
  const chunksRef   = useRef([]);

  // Start recording
  const startRecording = async () => {
    console.log('▶️ startRecording');
    setTranscript('');
    setSummary('');
    setStatus('recording');

    const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
    console.log('🔔 Stream obtained');
    streamRef.current = stream;
    videoRef.current.srcObject = stream;

    const audioStream = new MediaStream(stream.getAudioTracks());
    const recorder    = new MediaRecorder(audioStream);
    recorderRef.current = recorder;
    chunksRef.current = [];

    recorder.ondataavailable = e => {
      console.log('📥 chunk size', e.data.size);
      chunksRef.current.push(e.data);
    };
    recorder.onstop = handleStop;
    recorder.start();
  };

  // Stop & transcribe
  const stopRecording = () => {
    console.log('⏹ stopRecording');
    recorderRef.current?.stop();
    streamRef.current?.getTracks().forEach(t => t.stop());
    setStatus('transcribing');
  };

  // Handle stop event
  const handleStop = async () => {
    console.log('🛑 recorder stopped, chunks:', chunksRef.current.length);
    const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
    const fd   = new FormData();
    fd.append('audio', blob, 'meeting.webm');

    try {
      console.log('📤 sending to /api/transcribe');
      const res = await fetch('https://2nd-production-90db.up.railway.app/api/transcribe', { method: 'POST', body: fd });
      const data = await res.json();
      if (!data.transcript) throw new Error(data.error || 'No transcript');
      console.log('📋 Received transcript:', data.transcript.slice(0,100), '…');
      setTranscript(data.transcript);
    } catch (err) {
      console.error('❌ /api/transcribe failed', err);
      setTranscript('❌ Transcription failed.');
    } finally {
      setStatus('idle');
    }
  };

  // Generate summary
  const generateSummary = async () => {
    if (!transcript) return;
    console.log('📤 sending to /api/summarize');
    setStatus('summarizing');
    try {
      const res = await fetch('https://2nd-production-90db.up.railway.app/api/summarize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcript })
      });
      const data = await res.json();
      if (!data.summary) throw new Error(data.error || 'No summary');
      console.log('📝 Received summary:', data.summary.slice(0,100), '…');
      setSummary(data.summary);
    } catch (err) {
      console.error('❌ /api/summarize failed', err);
      setSummary('❌ Summarization failed.');
    } finally {
      setStatus('idle');
    }
  };

  // Share notes
  const share = async () => {
    console.log('📤 sharing notes');
    try {
      await fetch('https://2nd-production-90db.up.railway.app/api/share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcript, summary })
      });
      alert('✅ Notes shared!');
    } catch {
      alert('❌ Share failed.');
    }
  };

  // UI labels
  let recordLabel = 'Start Recording';
  if (status === 'recording')         recordLabel = 'Stop & Transcribe';
  else if (status === 'transcribing') recordLabel = 'Transcribing…';
  else if (status === 'summarizing')  recordLabel = 'Summarizing…';

  return (
    <div className="lexnote-page">
      <h2 className="lexnote-heading">Lex Note</h2>
      <p className="lexnote-subtitle">
        Capture, transcribe, summarize & share notes.<br/>
        Simple, yet powerful.
      </p>

      <video
        ref={videoRef}
        autoPlay
        muted
        className="lexnote-video"
        style={{ display: streamRef.current ? 'block' : 'none' }}
      />

      <div className="lexnote-actions">
        <button
          className="lexnote-button record-btn"
          onClick={status === 'recording' ? stopRecording : startRecording}
          disabled={status === 'transcribing' || status === 'summarizing'}
        >
          {recordLabel}
        </button>
        <button
          className="lexnote-button"
          onClick={generateSummary}
          disabled={!transcript || status !== 'idle'}
        >
          Generate Summary
        </button>
        <button
          className="lexnote-button secondary"
          onClick={share}
          disabled={!transcript && !summary}
        >
          Share
        </button>
      </div>

      <section className="lexnote-section">
        <h3>Summary</h3>
        <div className="lexnote-panel">
          {summary || <span className="lexnote-note">No summary yet</span>}
        </div>
      </section>

      <section className="lexnote-section">
        <h3>Transcript</h3>
        <div className="lexnote-panel">
          {transcript || <span className="lexnote-note">No transcript yet</span>}
        </div>
      </section>
    </div>
  );
}
